import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Task completion modal — opens when a task is marked complete from the
// project page. Before the task closes it makes sure the work is properly
// documented:
//   1. Business Unit + Activity — required so the time lands in the right
//      time-per-activity / per-BU rollup. One-off tasks only need an
//      Activity (they have no BU by design); everything else needs both.
//   2. Missing teammates — surfaces project members who aren't on the task
//      yet, in case someone who worked on it should be credited/assigned.
//   3. Time spent — shows the Deep Focus time already logged from the
//      caller's calendar blocks (the source of truth) and asks for any
//      ADDITIONAL time spent outside those blocks, in 15-min increments.
//
// On confirm it: patches the project's BU/Activity if changed, adds any
// selected teammates to the task, then PATCHes the task to completed with
// the extra minutes. The backend also enforces the BU/Activity gate as a
// backstop.
import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { useOrgCatalog } from "../hooks/useOrgCatalog";
function fmtMinutes(total) {
    if (total <= 0)
        return "0 min";
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0)
        return `${m} min`;
    if (m === 0)
        return `${h}h`;
    return `${h}h ${m}m`;
}
export default function TaskCompletionModal({ taskId, projectId, taskTitle, onClose, onCompleted, }) {
    const { user: viewer } = useAuth();
    const { businessUnits: units, activities } = useOrgCatalog();
    const [project, setProject] = useState(null);
    const [taskAssignees, setTaskAssignees] = useState([]);
    const [deepFocusMinutes, setDeepFocusMinutes] = useState(0);
    const [loading, setLoading] = useState(true);
    // Full assignable-user roster — admins see /users, everyone else
    // sees BU-scoped /users/coworkers (matches the Projects.tsx pattern).
    // Loaded lazily the first time the user opens the "Include someone
    // not on this project" picker.
    const [allUsers, setAllUsers] = useState(null);
    const [showOtherPicker, setShowOtherPicker] = useState(false);
    const [otherPickId, setOtherPickId] = useState("");
    const [businessUnitId, setBusinessUnitId] = useState("");
    const [activityId, setActivityId] = useState("");
    // Coworker credits: map of user_id → minutes credited. A user is
    // included in the add list iff their entry exists here (even if 0).
    // The number drives the per-teammate minutes picker — 0 means
    // "add them but no time credit", >0 means "add + create a Deep
    // Focus block of that duration on their calendar".
    const [coworkerCredits, setCoworkerCredits] = useState(new Map());
    const [extraMinutes, setExtraMinutes] = useState(0);
    // "When did you finish this?" — defaults to now (rendered as
    // local-time YYYY-MM-DDTHH:mm for the <input type="datetime-local">
    // widget). Sent to the backend as body.completed_at on PATCH /tasks
    // and as body.worked_at on each coworker assignees POST so credit
    // blocks land on the actual day the work happened.
    const [finishedAt, setFinishedAt] = useState(() => {
        const d = new Date();
        // Strip seconds + ms, zero-pad, render in local time so the
        // input shows what the user expects without TZ surprise.
        const p = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [proj, assignees, summary] = await Promise.all([
                    api.get(`/projects/${projectId}`),
                    api.get(`/tasks/${taskId}/assignees`),
                    api.get(`/tasks/${taskId}/time-summary`),
                ]);
                if (cancelled)
                    return;
                setProject(proj);
                setTaskAssignees(assignees);
                setDeepFocusMinutes(summary.deep_focus_minutes ?? 0);
                setBusinessUnitId(proj.business_unit_id ?? "");
                setActivityId(proj.activity_id ?? "");
            }
            catch (e) {
                if (!cancelled)
                    setError(e instanceof Error ? e.message : "Couldn't load completion details");
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [taskId, projectId]);
    const isOneOff = project?.type === "one_off_task";
    const requireBU = !isOneOff;
    // Project members who aren't already on the task — candidates to add so
    // nobody who worked on it gets missed.
    const missingTeammates = useMemo(() => {
        if (!project)
            return [];
        const onTask = new Set(taskAssignees.map((a) => a.user_id));
        const ids = project.assignee_ids ?? [];
        const names = project.assignee_names ?? [];
        const out = [];
        ids.forEach((id, i) => {
            if (!onTask.has(id))
                out.push({ id, name: names[i] ?? "Teammate" });
        });
        return out;
    }, [project, taskAssignees]);
    // Add a coworker to the credit map at 0 min (toggle off = remove
    // entry entirely). The minutes picker below updates the value.
    function toggleCowriter(id) {
        setCoworkerCredits((prev) => {
            const next = new Map(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.set(id, 0);
            return next;
        });
    }
    function setCoworkerMinutes(id, delta) {
        setCoworkerCredits((prev) => {
            const next = new Map(prev);
            const cur = next.get(id) ?? 0;
            next.set(id, Math.max(0, cur + delta));
            return next;
        });
    }
    // Lazy-load the user roster the first time the "Include someone
    // else" picker is opened. /users for admins, BU-scoped coworkers
    // for everyone else — same gating as Projects.tsx.
    async function ensureUsersLoaded() {
        if (allUsers !== null)
            return;
        try {
            const path = viewer?.role === "admin"
                ? "/users"
                : "/users/coworkers?include_self=true";
            const u = await api.get(path);
            setAllUsers(u);
        }
        catch {
            setAllUsers([]);
        }
    }
    function openOtherPicker() {
        setShowOtherPicker(true);
        void ensureUsersLoaded();
    }
    function confirmOtherPick() {
        if (!otherPickId)
            return;
        setCoworkerCredits((prev) => {
            const next = new Map(prev);
            if (!next.has(otherPickId))
                next.set(otherPickId, 0);
            return next;
        });
        setOtherPickId("");
        setShowOtherPicker(false);
    }
    const needsActivity = !activityId;
    const needsBU = requireBU && !businessUnitId;
    const canConfirm = !loading && !needsActivity && !needsBU && !saving;
    async function confirm() {
        if (!project)
            return;
        if (needsActivity || needsBU) {
            setError(`Set this work's ${[
                needsActivity ? "Activity" : null,
                needsBU ? "Business Unit" : null,
            ]
                .filter(Boolean)
                .join(" and ")} before completing it.`);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            // 1. Persist BU/Activity on the project if they changed.
            const buChanged = (project.business_unit_id ?? "") !== businessUnitId;
            const actChanged = (project.activity_id ?? "") !== activityId;
            if (buChanged || actChanged) {
                await api.patch(`/projects/${project.id}`, {
                    business_unit_id: businessUnitId || null,
                    activity_id: activityId || null,
                });
            }
            // Convert the local "When did you finish?" string to an ISO
            // datetime the backend can parse. The browser gives us
            // "YYYY-MM-DDTHH:mm" in LOCAL time — new Date() interprets that
            // correctly and toISOString() converts to UTC for the wire.
            const workedAtIso = (() => {
                if (!finishedAt)
                    return null;
                const d = new Date(finishedAt);
                if (Number.isNaN(d.getTime()))
                    return null;
                return d.toISOString();
            })();
            // 2. Add selected teammates to the task — with optional time
            // credit. minutes_credited > 0 triggers the backend to create a
            // Deep Focus block for that user so the credit flows through
            // Submitted Work Hours + Admin team-week rollup. worked_at
            // anchors the credit block at the user-picked finish time so
            // it lands on the right day on their calendar.
            for (const [uid, minutes] of coworkerCredits.entries()) {
                try {
                    await api.post(`/tasks/${taskId}/assignees`, {
                        user_id: uid,
                        minutes_credited: minutes > 0 ? minutes : null,
                        worked_at: workedAtIso,
                    });
                }
                catch (e) {
                    // 409 = already assigned (raced or pre-existing). Skip
                    // anything else bubbles. We don't try to re-credit when
                    // they're already on the task — that path needs a
                    // dedicated "log time for someone" endpoint.
                    if (!(e instanceof ApiError && e.status === 409))
                        throw e;
                }
            }
            // 3. Complete the task. completed_at backdates the close to
            // when the user said the work finished — picked up by the
            // accomplishments rollup, the wins feed, and the Admin team-
            // week table.
            await api.patch(`/tasks/${taskId}`, {
                status: "completed",
                extra_minutes_logged: extraMinutes,
                completed_at: workedAtIso,
            });
            await onCompleted();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't complete the task");
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, style: { zIndex: 250 }, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Complete task" }), _jsx("p", { className: "muted small", style: { marginTop: "-0.5rem" }, children: taskTitle }), loading ? (_jsx("p", { className: "muted small", children: "Loading\u2026" })) : (_jsxs(_Fragment, { children: [_jsxs("label", { children: ["Activity", " ", needsActivity && (_jsx("span", { className: "pill warn", style: { marginLeft: 4 }, children: "required" })), _jsxs("select", { value: activityId, onChange: (e) => setActivityId(e.target.value), children: [_jsx("option", { value: "", children: "\u2014 pick an activity \u2014" }), activities.map((a) => (_jsx("option", { value: a.id, children: a.name }, a.id)))] })] }), requireBU && (_jsxs("label", { children: ["Business unit", " ", needsBU && (_jsx("span", { className: "pill warn", style: { marginLeft: 4 }, children: "required" })), _jsxs("select", { value: businessUnitId, onChange: (e) => setBusinessUnitId(e.target.value), children: [_jsx("option", { value: "", children: "\u2014 pick a business unit \u2014" }), units.map((u) => (_jsx("option", { value: u.id, children: u.name }, u.id)))] })] })), _jsxs("label", { style: { marginTop: "0.5rem" }, children: ["When did you finish this?", _jsx("input", { type: "datetime-local", value: finishedAt, onChange: (e) => setFinishedAt(e.target.value), style: { marginTop: 4 } }), _jsx("span", { className: "muted small", style: { display: "block", marginTop: 2 }, children: "Backdates the close + any coworker credit so it lands on the day the work actually happened." })] }), _jsxs("div", { className: "block-mirror-prompt", children: [_jsxs("div", { className: "block-mirror-prompt-head", children: [_jsx("strong", { children: "Anyone else who worked on this?" }), _jsx("span", { className: "muted small", children: "Tick a teammate, then dial how many minutes to credit them. The credit drops a Deep Focus block on their calendar so it counts in their Submitted Work Hours." })] }), missingTeammates.length === 0 && coworkerCredits.size === 0 && (_jsx("p", { className: "muted small", children: "Everyone on the project is already on this task." })), (missingTeammates.length > 0 || coworkerCredits.size > 0) && (_jsx("ul", { className: "block-mirror-prompt-list", children: (() => {
                                        const known = new Map();
                                        missingTeammates.forEach((m) => known.set(m.id, m.name));
                                        // Pull names for ad-hoc picks (allUsers may already be
                                        // loaded; fall back to "Teammate" if not).
                                        coworkerCredits.forEach((_, id) => {
                                            if (!known.has(id)) {
                                                const fromAll = (allUsers ?? []).find((u) => u.id === id)?.name ??
                                                    "Teammate";
                                                known.set(id, fromAll);
                                            }
                                        });
                                        return Array.from(known.entries()).map(([id, name]) => {
                                            const picked = coworkerCredits.has(id);
                                            const minutes = coworkerCredits.get(id) ?? 0;
                                            return (_jsxs("li", { className: "cowriter-row", children: [_jsxs("label", { className: "inline-checkbox", children: [_jsx("input", { type: "checkbox", checked: picked, onChange: () => toggleCowriter(id) }), _jsx("span", { children: name })] }), picked && (_jsxs("div", { className: "row", style: {
                                                            alignItems: "center",
                                                            gap: "0.4rem",
                                                            marginLeft: "1.6rem",
                                                            marginTop: 4,
                                                        }, children: [_jsx("span", { className: "muted small", children: "Credit them:" }), _jsx("button", { type: "button", className: "duration-btn", onClick: () => setCoworkerMinutes(id, -15), disabled: minutes <= 0, "aria-label": `Subtract 15 minutes for ${name}`, children: "\u221215" }), _jsx("strong", { style: {
                                                                    minWidth: "4.5rem",
                                                                    textAlign: "center",
                                                                }, children: fmtMinutes(minutes) }), _jsx("button", { type: "button", className: "duration-btn", onClick: () => setCoworkerMinutes(id, 15), "aria-label": `Add 15 minutes for ${name}`, children: "+15" })] }))] }, id));
                                        });
                                    })() })), !showOtherPicker ? (_jsx("button", { type: "button", className: "btn-link", onClick: openOtherPicker, style: { marginTop: "0.4rem" }, children: "+ Include someone not on this project" })) : (_jsxs("div", { className: "row", style: {
                                        alignItems: "center",
                                        gap: "0.4rem",
                                        marginTop: "0.4rem",
                                    }, children: [_jsxs("select", { value: otherPickId, onChange: (e) => setOtherPickId(e.target.value), children: [_jsx("option", { value: "", children: allUsers === null
                                                        ? "Loading…"
                                                        : "— pick a teammate —" }), (allUsers ?? [])
                                                    .filter((u) => {
                                                    // Skip anyone already in the candidates list or
                                                    // already on the task — we only need the OUTER
                                                    // set here.
                                                    const onTask = taskAssignees.some((a) => a.user_id === u.id);
                                                    const inMissing = missingTeammates.some((m) => m.id === u.id);
                                                    return !onTask && !inMissing;
                                                })
                                                    .map((u) => (_jsx("option", { value: u.id, children: u.name }, u.id)))] }), _jsx("button", { type: "button", className: "btn-link", onClick: confirmOtherPick, disabled: !otherPickId, children: "Add" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => {
                                                setShowOtherPicker(false);
                                                setOtherPickId("");
                                            }, children: "Cancel" })] }))] }), _jsxs("div", { className: "flex-banner", style: { marginTop: "0.5rem" }, children: [_jsx("div", { className: "flex-title", children: "Time spent" }), _jsxs("div", { className: "flex-desc", children: [_jsx("strong", { children: fmtMinutes(deepFocusMinutes) }), " logged from your Deep Focus blocks on the calendar."] })] }), _jsxs("label", { children: ["Any additional time spent outside Deep Focus blocks?", _jsxs("div", { className: "row", style: { alignItems: "center", gap: "0.5rem", marginTop: 4 }, children: [_jsx("button", { type: "button", className: "duration-btn", onClick: () => setExtraMinutes((m) => Math.max(0, m - 15)), disabled: extraMinutes <= 0, "aria-label": "Subtract 15 minutes", children: "\u221215" }), _jsx("strong", { style: { minWidth: "5rem", textAlign: "center" }, children: fmtMinutes(extraMinutes) }), _jsx("button", { type: "button", className: "duration-btn", onClick: () => setExtraMinutes((m) => m + 15), "aria-label": "Add 15 minutes", children: "+15" })] })] }), _jsxs("p", { className: "muted small", style: { marginTop: 4 }, children: ["Total credited:", " ", _jsx("strong", { children: fmtMinutes(deepFocusMinutes + extraMinutes) })] })] })), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: onClose, disabled: saving, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", onClick: confirm, disabled: !canConfirm, title: needsActivity || needsBU
                                ? "Set the activity / business unit first"
                                : "Mark this task complete", children: saving ? "Completing…" : "Complete task" })] })] }) }));
}
